'use client';

import { faArrowRightLong } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Box, Card, CardContent, IconButton, Typography } from '@mui/material';
import { format } from 'date-fns';
import { vi } from 'date-fns/locale';
import { useEffect, useRef, useState } from 'react';

import { Button, OptimizedImage } from '@/components/atoms';
import OrangeLine from '@/components/atoms/OrangeLine';
import { SectionContainer } from '@/components/layouts/SectionContainer';
import { getStrapiMediaUrl } from '@/services/api/base';
import { FeaturedNewsSectionData } from '@/services/types/landing-page';

interface NewsSectionProps {
  data?: FeaturedNewsSectionData;
  className?: string;
  id?: string; // Optional ID for easier debugging
}

export default function NewsSection({
  data,
  className = '',
  id,
}: NewsSectionProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showNavigation, setShowNavigation] = useState(false);
  const sliderRef = useRef<HTMLDivElement>(null);

  const articles = data?.articles || [];
  const totalArticles = articles.length;

  useEffect(() => {
    setShowNavigation(totalArticles > 1);
  }, [totalArticles]);

  const goToPrevious = () => {
    setCurrentIndex(prev => (prev > 0 ? prev - 1 : totalArticles - 1));
  };

  const goToNext = () => {
    setCurrentIndex(prev => (prev < totalArticles - 1 ? prev + 1 : 0));
  };

  useEffect(() => {
    if (sliderRef.current && totalArticles > 1) {
      const cardWidth = 630; // 600px card + 30px gap
      const scrollPosition = currentIndex * cardWidth;

      sliderRef.current.scrollTo({
        left: scrollPosition,
        behavior: 'smooth',
      });
    }
  }, [currentIndex, totalArticles]);

  // Auto-advance every 5 seconds
  useEffect(() => {
    if (!showNavigation) return;

    const interval = setInterval(() => {
      goToNext();
    }, 5000);

    return () => clearInterval(interval);
  }, [showNavigation, currentIndex]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        goToPrevious();
      } else if (event.key === 'ArrowRight') {
        goToNext();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  if (totalArticles === 0) {
    return null;
  }

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, 'EEEE, dd/MM/yyyy', { locale: vi });
    } catch {
      return dateString;
    }
  };

  const _getCategoryName = (
    categories: Array<{ name: string; slug: string }>
  ) => {
    return categories && categories.length > 0 ? categories[0].name : '';
  };

  // Helper function to get image URL with fallback
  const getArticleImageUrl = (article: any) => {
    if (article.featuredImage?.url) {
      return getStrapiMediaUrl(article.featuredImage.url);
    }
    return '/default-news-image.jpg';
  };

  return (
    <SectionContainer>
      <Box
        component='section'
        id={id || 'news-section'}
        data-component='news-section'
        data-total-articles={totalArticles}
        data-current-index={currentIndex}
        data-show-navigation={showNavigation}
        data-has-data={!!data}
        sx={{
          py: { xs: 6, md: 10 },
        }}
        className={className}
      >
        {/* Section Header */}
        <Box
          data-component='news-section-header'
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: { xs: 4, md: 6 },
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box data-component='news-section-title-container'>
            <Typography
              variant='h2'
              component='h2'
              data-component='news-section-title'
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                fontSize: { xs: '28px', md: '36px' },
                lineHeight: 1.53,
                color: '#001A44',
                mb: 1,
                textTransform: 'uppercase',
              }}
            >
              {data?.title || 'TIN TỨC'}
            </Typography>
            <OrangeLine width='100%' height='3px' mb={2} />
          </Box>

          {/* View More Button */}
          <Button
            variant='text'
            href={data?.viewMoreLink || '/news'}
            data-component='news-section-view-more-button'
            icon={
              <FontAwesomeIcon
                icon={faArrowRightLong}
                style={{ width: '16px', height: '16px' }}
              />
            }
            iconPosition='end'
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              fontSize: '16px',
              textTransform: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {data?.viewMoreText || 'Xem thêm'}
          </Button>
        </Box>

        {/* News Articles Row */}
        <Box
          ref={sliderRef}
          data-component='news-section-slider'
          data-current-index={currentIndex}
          data-total-articles={totalArticles}
          sx={{
            display: 'flex',
            gap: '30px',
            mb: 6,
            overflowX: 'auto',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
          }}
        >
          {articles.map((article, index) => (
            <Card
              key={index}
              data-component='news-article-card'
              data-article-index={index}
              data-article-slug={article.slug}
              data-article-title={article.title}
              aria-label={`Read article: ${article.title}`}
              sx={{
                minWidth: '600px',
                height: '583px',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 0,
                border: '1px solid rgba(0, 0, 0, 0.1)',
                boxShadow: 'none',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0px 8px 30px rgba(0, 0, 0, 0.15)',
                  border: '1px solid #EB722E',
                  '& .news-article-title': {
                    color: '#EB722E',
                  },
                  '& .news-article-content::before': {
                    height: '8px',
                  },
                },
              }}
              component='a'
              href={`/news/${article.slug}`}
              style={{ textDecoration: 'none' }}
            >
              {/* Optimized Article Image using Next.js Image */}
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: '320px',
                  overflow: 'hidden',
                }}
              >
                <OptimizedImage
                  src={getArticleImageUrl(article)}
                  alt={article.title}
                  fill
                  sizes='(max-width: 600px) 100vw, 600px'
                  style={{
                    objectFit: 'cover',
                    objectPosition: 'center',
                  }}
                  priority={index === 0} // Priority loading for first image
                  fallbackSrc='/default-news-image.jpg'
                />
              </Box>

              <CardContent
                sx={{
                  flexGrow: 1,
                  p: 3,
                  height: '263px', // Fixed height for content (583px total - 320px image = 263px)
                  minHeight: '263px', // Ensure minimum height
                  maxHeight: '263px', // Ensure maximum height
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
              >
                {/* Date */}
                <Typography
                  variant='body2'
                  data-component='news-article-date'
                  data-published-date={article.publishedAt}
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 400,
                    fontSize: '16px',
                    color: '#9D9D9D',
                    display: 'block',
                    mb: 2,
                  }}
                >
                  {formatDate(article.publishedAt)}
                </Typography>

                {/* Title */}
                <Typography
                  variant='h5'
                  component='h3'
                  data-component='news-article-title'
                  className='news-article-title'
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 700,
                    fontSize: '24px',
                    lineHeight: 1.21,
                    mb: 2,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    minHeight: '58px',
                    color: '#220000',
                    transition: 'color 0.3s ease',
                  }}
                >
                  {article.title}
                </Typography>

                {/* Excerpt */}
                <Typography
                  variant='body2'
                  data-component='news-article-excerpt'
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 400,
                    fontSize: '16px',
                    lineHeight: 1.25,
                    color: '#555555',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {article.excerpt}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* Pagination */}
        {showNavigation && (
          <Box
            data-component='news-section-navigation'
            data-current-index={currentIndex}
            data-total-articles={totalArticles}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Typography
              variant='body2'
              data-component='news-section-counter'
              sx={{
                fontFamily: 'var(--font-inter), Inter, sans-serif',
                fontWeight: 400,
                fontSize: {
                  xs: '0.75rem',
                  lg: '14px',
                },
                lineHeight: 1.21,
                color: 'primary.main',
                textTransform: 'uppercase',
                minWidth: '38px',
                textAlign: 'center',
              }}
            >
              {currentIndex + 1} - {totalArticles}
            </Typography>

            <Box
              data-component='news-section-progress-bar'
              sx={{
                width: { xs: '120px', sm: '180px', lg: '240px' },
                height: '2px',
                backgroundColor: 'rgba(0, 0, 0, 0.25)',
                position: 'relative',
                borderRadius: '1px',
              }}
            >
              <Box
                data-component='news-section-progress-fill'
                sx={{
                  height: '100%',
                  backgroundColor: 'primary.main',
                  borderRadius: '1px',
                  transition: 'width 0.5s ease-out',
                  width: `${((currentIndex + 1) / totalArticles) * 100}%`,
                }}
              />
            </Box>

            <IconButton
              onClick={goToPrevious}
              data-component='news-section-prev-button'
              aria-label='Previous news article'
              sx={{
                width: '24px',
                height: '24px',
                color: 'secondary.main',
                '&:hover': {
                  backgroundColor: 'rgba(255, 94, 19, 0.1)',
                },
              }}
            >
              <ChevronLeft sx={{ fontSize: '24px' }} />
            </IconButton>

            <IconButton
              onClick={goToNext}
              data-component='news-section-next-button'
              aria-label='Next news article'
              sx={{
                width: '24px',
                height: '24px',
                color: 'secondary.main',
                '&:hover': {
                  backgroundColor: 'rgba(255, 94, 19, 0.1)',
                },
              }}
            >
              <ChevronRight sx={{ fontSize: '24px' }} />
            </IconButton>
          </Box>
        )}
      </Box>
    </SectionContainer>
  );
}
